import Seo from '@/components/Seo';
import { Reveal } from '@/components/motion';
import { CONTACT } from '@/content/site';

const SECTIONS = [
  {
    h: 'Who we are',
    p: [
      'This policy describes how KTK Advisors ("we", "us") handles information gathered through this website. KTK Advisors is a founder-led advisory practice helping businesses reduce expenses, grow revenue, and improve operations.',
    ],
  },
  {
    h: 'Information we collect',
    p: [
      'Contact form: when you submit our consultation form, we collect the information you provide—your name, company, work email, optional phone number, area of interest, and a brief description of your challenge. We use this information for one purpose: to respond to your inquiry and, if you choose to move forward, to prepare for an engagement.',
      'Analytics: this website uses a privacy-focused analytics tool that measures page visits and general usage patterns. Analytics are configured so that individual visitors are not profiled beyond what is needed to understand aggregate site usage.',
    ],
  },
  {
    h: 'How information is handled',
    p: [
      'Consultation requests are transmitted to KTK Advisors by email and retained only as long as needed to respond to your inquiry and manage any resulting relationship. We do not sell, rent, or share your information with third parties for their own marketing purposes.',
      'We do not send marketing email lists or newsletters. If you correspond with us, we will use your contact details to reply to you directly.',
    ],
  },
  {
    h: 'What not to send',
    p: [
      'Please do not include patient information, financial account details, or other sensitive records in website inquiries. Our services rarely require sensitive documents to start a conversation, and we will tell you plainly if something specific is needed at any point.',
    ],
  },
  {
    h: 'Cookies',
    p: [
      'The website uses essential cookies and analytics cookies as described above. You can block or delete cookies through your browser settings; the site will continue to function.',
    ],
  },
  {
    h: 'Your choices',
    p: [
      'You may ask us what information we hold about you, request corrections, or ask us to delete correspondence—by contacting us using the details below. We will honor reasonable requests consistent with our need to retain records of business engagements.',
    ],
  },
  {
    h: 'Changes to this policy',
    p: [
      'If our practices change, this policy will be updated to reflect them. The effective date below shows the current version.',
    ],
  },
];

export default function Privacy() {
  return (
    <>
      <Seo
        title="Privacy Policy | KTK Advisors"
        description="How KTK Advisors collects, uses, and protects information submitted through this website, including consultation requests and analytics."
        path="/privacy"
      />

      <section className="relative overflow-hidden bg-deep" data-testid="privacy-hero">
        <div className="aura aura--emerald" style={{ width: 560, height: 560, top: -220, left: -140 }} />
        <div className="wrap pt-40 pb-20 lg:pt-48 lg:pb-24 relative">
          <Reveal>
            <p className="eyebrow-mono">Privacy Policy</p>
            <h1 className="h1 mt-7 max-w-[20ch]">Plain words about your information.</h1>
            <p className="lede mt-8 max-w-[54ch]">
              This policy describes what this website collects, why, and what
              happens to it. Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep section" data-testid="privacy-body">
        <div className="wrap grid lg:grid-cols-[1fr_2fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">The Policy</p>
          </Reveal>
          <div className="measure space-y-10">
            {SECTIONS.map((s) => (
              <Reveal key={s.h}>
                <h2 className="h3">{s.h}</h2>
                <div className="mt-4 space-y-4">
                  {s.p.map((para, i) => (
                    <p key={i} className="body-copy text-[15.5px] text-mist">
                      {para}
                    </p>
                  ))}
                </div>
              </Reveal>
            ))}
            <Reveal>
              <h2 className="h3">Contact</h2>
              <p className="body-copy text-[15.5px] text-mist mt-4">
                Questions about this policy can be directed to {CONTACT.founder} at{' '}
                <a href={CONTACT.emailHref} data-testid="privacy-email-link" className="font-semibold text-goldbright break-all">
                  {CONTACT.email}
                </a>{' '}
                or by phone at{' '}
                <a href={CONTACT.phoneHref} data-testid="privacy-phone-link" className="font-semibold text-goldbright">
                  {CONTACT.phone}
                </a>
                , or by mail at {CONTACT.address1}, {CONTACT.address2}.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
